import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Row, Space, Spin, Tag, Typography } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Hls from 'hls.js';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { fetchAuthSession } from '../auth/gateway';
import { useDevice } from '../contexts/DeviceContext';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { API_CONFIG } from '../config/api';
import LocationAnalysisMap from '../components/location-analysis/LocationAnalysisMap';
import useLocationAnalysisDerivedData from '../components/location-analysis/useLocationAnalysisDerivedData';
import { buildDayRange } from '../components/location-analysis/dateUtils';
import { secureLogger } from '../utils/secureLogger';

const { Text, Title } = Typography;

type DiarySummaryEvent = {
  start: string;
  end: string;
  title: string;
  detail: string;
  hasData?: number | boolean;
};

type DiarySummaryRecord = {
  status: string;
  summary?: { events?: DiarySummaryEvent[] } | null;
  error_message?: string | null;
  updated_at?: string | null;
};

type ConversationSummaryRecord = {
  status: string;
  summary?: Record<string, unknown> | null;
  input_stats?: Record<string, unknown> | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
};

type ConversationTopicSummary = {
  topic: string;
  summary: string;
  ainote: string | null;
};

type TimelapseJob = {
  status: string;
  job_id?: string | null;
  video_url?: string | null;
};

type TimelapseMediaCookieResponse = {
  enabled?: boolean;
  expires_at?: number | null;
};

const resolveMediaType = (url: string) => {
  const path = (() => {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  })().toLowerCase();
  return path.endsWith('.mp4') ? 'video/mp4' : 'application/x-mpegurl';
};

const TimelapseVideo: React.FC<{ src: string; initialTimeSec?: number | null }> = ({ src, initialTimeSec }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaType = resolveMediaType(src);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof initialTimeSec !== 'number' || !Number.isFinite(initialTimeSec)) {
      return;
    }
    const seek = () => {
      video.currentTime = Math.max(0, initialTimeSec);
    };
    if (video.readyState >= 1) {
      seek();
      return;
    }
    video.addEventListener('loadedmetadata', seek, { once: true });
    return () => video.removeEventListener('loadedmetadata', seek);
  }, [initialTimeSec, src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || mediaType !== 'application/x-mpegurl') {
      return;
    }
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      return;
    }
    if (!Hls.isSupported()) {
      return;
    }
    const hls = new Hls({
      xhrSetup: (xhr) => {
        xhr.withCredentials = true;
      },
    });
    hls.loadSource(src);
    hls.attachMedia(video);
    return () => hls.destroy();
  }, [mediaType, src]);

  return (
    <video
      ref={videoRef}
      controls
      playsInline
      src={mediaType === 'video/mp4' ? src : undefined}
      style={{
        width: '100%',
        aspectRatio: '4 / 3',
        borderRadius: 8,
        background: '#0f172a',
        display: 'block',
      }}
    />
  );
};

const DiaryEventDetailPage: React.FC = () => {
  const { eventIndex } = useParams<{ eventIndex: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { selectedDevice } = useDevice();
  const { setPageHeader } = usePageHeader();
  const date = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Tokyo',
    [],
  );
  const [summary, setSummary] = useState<DiarySummaryRecord | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [timelapseJob, setTimelapseJob] = useState<TimelapseJob | null>(null);
  const [timelapseLoading, setTimelapseLoading] = useState(false);
  const [cookieReady, setCookieReady] = useState(false);
  const [cookieError, setCookieError] = useState<string | null>(null);
  const [conversationSummary, setConversationSummary] = useState<ConversationSummaryRecord | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationSubmitting, setConversationSubmitting] = useState(false);

  useEffect(() => {
    setPageHeader({
      title: `過去の日記（${dayjs(date).format('YYYY/MM/DD')}）`,
    });
    return () => setPageHeader(null);
  }, [date, setPageHeader]);

  const locationRange = useMemo(() => buildDayRange(dayjs(date)), [date]);
  const locationAnalysis = useLocationAnalysisDerivedData({
    deviceId: selectedDevice?.id,
    rangeStartIso: locationRange.startIso,
    rangeEndIso: locationRange.endIso,
  });

  const eventNumber = Number(eventIndex);
  const diaryEvents = summary?.summary?.events ?? [];
  const event = Number.isInteger(eventNumber) ? diaryEvents[eventNumber] : undefined;
  const returnTo = `/dairy?date=${encodeURIComponent(date)}`;
  const state = location.state as { playbackTimeSec?: number } | null;
  const eventHasConversationData = event?.hasData !== 0 && event?.hasData !== false;
  const conversationStatus = conversationSummary?.status ?? 'not_started';
  const conversationRunning = ['queued', 'running'].includes(conversationStatus);
  const conversationButtonLabel = conversationSummary ? '再生成' : '生成';
  const conversationPayload = conversationSummary?.summary ?? null;
  const conversationTopicSummaries = useMemo(() => {
    if (!conversationPayload || typeof conversationPayload !== 'object') {
      return null;
    }
    const payload = conversationPayload as Record<string, unknown>;
    const summaries = payload.summaries;
    if (!Array.isArray(summaries)) {
      return null;
    }
    const parsed = summaries
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }
        const entry = item as Record<string, unknown>;
        const topic =
          (typeof entry.topic === 'string' && entry.topic.trim()) ||
          (typeof entry.title === 'string' && entry.title.trim()) ||
          '話題';
        const summaryText =
          (typeof entry.summary === 'string' && entry.summary.trim()) ||
          (typeof entry.text === 'string' && entry.text.trim()) ||
          null;
        const ainote =
          (typeof entry.ainote === 'string' && entry.ainote.trim()) ||
          (typeof entry.ai_note === 'string' && entry.ai_note.trim()) ||
          null;
        if (!summaryText) {
          return null;
        }
        return { topic, summary: summaryText, ainote };
      })
      .filter((item): item is ConversationTopicSummary => Boolean(item));
    return parsed.length ? parsed : null;
  }, [conversationPayload]);
  const conversationSummaryText = useMemo(() => {
    if (!conversationPayload) {
      return null;
    }
    if (typeof conversationPayload === 'string') {
      return conversationPayload;
    }
    if (typeof conversationPayload === 'object') {
      const payload = conversationPayload as Record<string, unknown>;
      if (typeof payload.text === 'string' && payload.text.trim()) {
        return payload.text;
      }
    }
    return JSON.stringify(conversationPayload, null, 2);
  }, [conversationPayload]);
  const conversationTodos = useMemo(() => {
    if (!conversationPayload || typeof conversationPayload !== 'object') {
      return null;
    }
    const payload = conversationPayload as Record<string, unknown>;
    const todoValue = payload.todo;
    if (Array.isArray(todoValue)) {
      const items = todoValue
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item.length > 0);
      return items.length ? items : null;
    }
    if (typeof todoValue === 'string' && todoValue.trim()) {
      return [todoValue.trim()];
    }
    return null;
  }, [conversationPayload]);

  const fetchSummary = useCallback(async () => {
    if (!selectedDevice || !date) {
      return;
    }
    setSummaryLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('No token for diary event summary');
      }
      const params = new URLSearchParams({ day: date, timezone: timeZone });
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-summary?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        throw new Error(`Diary summary request failed: ${response.status}`);
      }
      setSummary(await response.json());
    } catch (error) {
      secureLogger.error('Failed to load diary event summary', { error: 'Diary event summary fetch failed' });
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [date, selectedDevice, timeZone]);

  const fetchTimelapse = useCallback(async () => {
    if (!selectedDevice || !date) {
      return;
    }
    setTimelapseLoading(true);
    setCookieReady(false);
    setCookieError(null);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('No token for timelapse');
      }
      const params = new URLSearchParams({ day: date, timezone: timeZone });
      const statusResponse = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/timelapse?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!statusResponse.ok) {
        throw new Error(`Timelapse status failed: ${statusResponse.status}`);
      }
      const job = (await statusResponse.json()) as TimelapseJob;
      setTimelapseJob(job);
      if (job.status !== 'succeeded' || !job.video_url) {
        return;
      }
      const cookieResponse = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/timelapse/media-cookie?${params.toString()}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        },
      );
      if (!cookieResponse.ok) {
        throw new Error(`Timelapse cookie failed: ${cookieResponse.status}`);
      }
      const cookiePayload = (await cookieResponse.json()) as TimelapseMediaCookieResponse;
      if (cookiePayload.enabled === false) {
        throw new Error('Timelapse cookie disabled');
      }
      setCookieReady(true);
    } catch (error) {
      secureLogger.error('Failed to load diary event timelapse', { error: 'Diary event timelapse fetch failed' });
      setCookieError('タイムラプスを読み込めません');
    } finally {
      setTimelapseLoading(false);
    }
  }, [date, selectedDevice, timeZone]);

  const fetchConversationSummary = useCallback(
    async (targetEvent: DiarySummaryEvent) => {
      if (!selectedDevice || !date) {
        return;
      }
      setConversationLoading(true);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          throw new Error('No token for conversation summary');
        }
        const params = new URLSearchParams({
          day: date,
          start: targetEvent.start,
          end: targetEvent.end,
          timezone: timeZone,
        });
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-conversation-summary?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.status === 404) {
          setConversationSummary(null);
          return;
        }
        if (!response.ok) {
          throw new Error(`Conversation summary request failed: ${response.status}`);
        }
        setConversationSummary(await response.json());
      } catch (error) {
        secureLogger.error('Failed to load diary event conversation summary', {
          error: 'Diary event conversation summary fetch failed',
        });
        setConversationSummary(null);
      } finally {
        setConversationLoading(false);
      }
    },
    [date, selectedDevice, timeZone],
  );

  const requestConversationSummary = useCallback(async () => {
    if (!selectedDevice || !date || !event || !eventHasConversationData) {
      return;
    }
    setConversationSubmitting(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('No token for conversation summary request');
      }
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-conversation-summary`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            day: date,
            start: event.start,
            end: event.end,
            timezone: timeZone,
          }),
        },
      );
      if (response.ok) {
        setConversationSummary(await response.json());
      } else if (response.status === 409) {
        await fetchConversationSummary(event);
      } else {
        throw new Error(`Conversation summary submit failed: ${response.status}`);
      }
    } catch (error) {
      secureLogger.error('Failed to submit diary event conversation summary', {
        error: 'Diary event conversation summary submit failed',
      });
    } finally {
      setConversationSubmitting(false);
    }
  }, [date, event, eventHasConversationData, fetchConversationSummary, selectedDevice, timeZone]);

  useEffect(() => {
    void fetchSummary();
    void fetchTimelapse();
  }, [fetchSummary, fetchTimelapse]);

  useEffect(() => {
    setConversationSummary(null);
    if (event && eventHasConversationData) {
      void fetchConversationSummary(event);
    }
  }, [event, eventHasConversationData, fetchConversationSummary]);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          padding: '10px 14px',
          background: '#e6f4ff',
          border: '1px solid #91caff',
        }}
      >
        <Link to={returnTo}>
          <Button icon={<LeftOutlined />} type="primary">
            {dayjs(date).format('YYYY/MM/DD')} の日記に戻る
          </Button>
        </Link>
        <Text strong>
          {event ? `${event.start} - ${event.end} ${event.title}` : 'イベント詳細'}
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6}>
          <Card title="タイムラプス" size="small">
            <Spin spinning={timelapseLoading}>
              {cookieReady && timelapseJob?.video_url ? (
                <TimelapseVideo src={timelapseJob.video_url} initialTimeSec={state?.playbackTimeSec} />
              ) : cookieError ? (
                <Alert type="warning" showIcon message={cookieError} />
              ) : (
                <Empty description={timelapseJob?.status === 'succeeded' ? '動画URLなし' : 'タイムラプス未生成'} />
              )}
            </Spin>
            {typeof state?.playbackTimeSec === 'number' && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                遷移時の再生位置: {Math.round(state.playbackTimeSec)}秒
              </Text>
            )}
          </Card>
          <Card title="地図" size="small" style={{ marginTop: 16 }}>
            <Spin spinning={locationAnalysis.loading}>
              <LocationAnalysisMap
                title="Event location"
                staypoints={locationAnalysis.staypoints}
                triplegs={locationAnalysis.triplegs}
                trips={locationAnalysis.trips}
                locations={locationAnalysis.visibleLocations}
                locationExtentBufferM={locationAnalysis.locationExtentBufferM}
                height={320}
              />
            </Spin>
          </Card>
        </Col>
        <Col xs={24} lg={18}>
          <Card size="small">
            <Spin spinning={summaryLoading}>
              {event ? (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <div>
                    <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
                      {event.title}
                    </Title>
                    <Space wrap>
                      <Tag color="blue">{event.start} - {event.end}</Tag>
                      {event.hasData === 0 || event.hasData === false ? (
                        <Tag>データ無し</Tag>
                      ) : (
                        <Tag color="green">データあり</Tag>
                      )}
                    </Space>
                  </div>
                  <Card title="イベント概要" size="small" style={{ background: '#fafafa' }}>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{event.detail || '概要はまだありません'}</Text>
                  </Card>
                  <Card
                    title="会話要約"
                    size="small"
                    extra={
                      eventHasConversationData ? (
                        <Button
                          size="small"
                          loading={conversationSubmitting}
                          disabled={conversationRunning}
                          onClick={requestConversationSummary}
                        >
                          {conversationButtonLabel}
                        </Button>
                      ) : null
                    }
                  >
                    <Spin spinning={conversationLoading}>
                      {!eventHasConversationData ? (
                        <Empty description="このイベントに会話データはありません" />
                      ) : conversationRunning ? (
                        <Empty description="会話要約生成中" />
                      ) : conversationStatus === 'failed' ? (
                        <Alert
                          type="warning"
                          showIcon
                          message="会話要約生成失敗"
                          description={conversationSummary?.error_message || undefined}
                        />
                      ) : conversationTopicSummaries ? (
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                          {conversationTopicSummaries.map((topic, index) => (
                            <div
                              key={`${topic.topic}-${index}`}
                              style={{ paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}
                            >
                              <Text strong>{topic.topic}</Text>
                              <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{topic.summary}</div>
                              {topic.ainote && (
                                <Alert
                                  type="info"
                                  showIcon
                                  message="AI note"
                                  description={topic.ainote}
                                  style={{ marginTop: 8 }}
                                />
                              )}
                            </div>
                          ))}
                          {conversationTodos && (
                            <Card title="TODO" size="small" style={{ background: '#fafafa' }}>
                              <Space direction="vertical" size={4}>
                                {conversationTodos.map((todo, index) => (
                                  <Text key={`${todo}-${index}`}>{todo}</Text>
                                ))}
                              </Space>
                            </Card>
                          )}
                        </Space>
                      ) : conversationSummaryText ? (
                        <Text style={{ whiteSpace: 'pre-wrap' }}>{conversationSummaryText}</Text>
                      ) : (
                        <Empty description="会話要約未生成" />
                      )}
                    </Spin>
                  </Card>
                  <Card title="今後追加する詳細" size="small">
                    <Space wrap>
                      <Tag>音声/話者</Tag>
                      <Tag>写真</Tag>
                      <Tag>飲食</Tag>
                      <Tag>決済</Tag>
                      <Tag>運動量</Tag>
                    </Space>
                  </Card>
                </Space>
              ) : (
                <Empty description={summary?.error_message || 'イベントが見つかりません'} />
              )}
            </Spin>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default DiaryEventDetailPage;
